// Phase 6+ — quick-range presets for the Run page date pickers.
//
// All helpers take a `today` Date so the tests can pin a reference date
// instead of mocking the system clock. Production callers pass
// `new Date()`. Each helper returns `{ start, end }` with ISO
// `YYYY-MM-DD` strings ready to drop into `<input type="date" />`.
//
// The "Last N days" math uses the user-facing convention `start =
// today - N days, end = today` (so "Last 7 days" yields an 8-day
// inclusive window). The user spec explicitly stated this; if the
// convention is ever revisited, update `lastNDaysRange` AND the
// matching test rows in `dateRanges.test.js`.

export function toIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function lastNDaysRange(today, n) {
  const start = new Date(today);
  start.setDate(start.getDate() - n);
  return { start: toIso(start), end: toIso(today) };
}

export function thisMonthRange(today) {
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return { start: toIso(start), end: toIso(today) };
}

export function lastMonthRange(today) {
  // Day 0 of the current month is the last day of the previous month —
  // works across leap-year Februaries and year rollovers without a
  // calendar lookup.
  const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const end   = new Date(today.getFullYear(), today.getMonth(), 0);
  return { start: toIso(start), end: toIso(end) };
}

export const QUICK_RANGE_OPTIONS = [
  { value: "custom",  label: "Custom" },
  { value: "last3",   label: "Last 3 days" },
  { value: "last7",   label: "Last 7 days" },
  { value: "last30",  label: "Last 30 days" },
  { value: "last90",  label: "Last 90 days" },
  { value: "thismo",  label: "This month" },
  { value: "lastmo",  label: "Last month" },
];

// Single dispatcher so Run.jsx doesn't need to import every helper
// individually. Returns `null` for "custom" / unknown keys so the
// caller's "if (r) setStart/setEnd" branch stays clean.
export function computeRange(quickKey, today = new Date()) {
  switch (quickKey) {
    case "last3":  return lastNDaysRange(today, 3);
    case "last7":  return lastNDaysRange(today, 7);
    case "last30": return lastNDaysRange(today, 30);
    case "last90": return lastNDaysRange(today, 90);
    case "thismo": return thisMonthRange(today);
    case "lastmo": return lastMonthRange(today);
    default:       return null;
  }
}
